import styled from '@emotion/styled';
import { Suspense } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';

import type {
  CountryCodeEnum,
  FreeTextbooksSyllabusSelectorCountrySelectorQuery,
} from '__free_textbooks_generated__/FreeTextbooksSyllabusSelectorCountrySelectorQuery.graphql';
import ErrorBoundaryWithRetry from 'ms-components/ErrorBoundaryWithRetry';
import PageDescription from 'ms-helpers/PageDescription';
import PageTitle from 'ms-helpers/PageTitle';
import { useSnowplow } from 'ms-helpers/Snowplow/useSnowplow';
import { FreeTextbooksBookDemoButton } from 'ms-pages/FreeTextbooks/FreeTextbooksBookDemoButton';
import * as FLAGS from 'ms-pages/FreeTextbooks/FreeTextbooksSyllabusSelectorFooter/assets/flags';
import MinorSpinner from 'ms-pages/Teacher/components/MinorSpinner';
import { font, fontFamily, fontSize, lineHeight } from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import LinkButton from 'ms-ui-primitives/Button/ReactRouterLinkButton';
import Separator from 'ms-ui-primitives/Separator';

import {
  FlagsWrapper,
  ButtonFlagWrapper,
  FlagWrapper,
  FlagLabel,
  Flag,
} from '../elements';
import seo from '../seo';
import { getCurriculumSelectorUrl } from '../urls';

const Title = styled.h1({
  ...font.gilroyHeading,
  fontSize: fontSize.xLarge,
});

const Subtitle = styled.div({
  fontSize: fontSize.medium,
  fontFamily: fontFamily.body,
});
const Page = styled.div({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '80vh',
  justifyContent: 'center',
  alignItems: 'center',
  color: colors.cloudBurst,
});

const styles = {
  almond50: {
    color: colors.almond50,
  },
  stretch: {
    alignItems: 'stretch',
  },
};

const ContactUsWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap',
  fontFamily: fontFamily.body,
  color: colors.neutralGray,
  fontSize: fontSize.medium,
  lineHeight: lineHeight.body,
});

const BookDemoWrapper = styled.div({
  paddingLeft: 48,
});

const query = graphql`
  query FreeTextbooksSyllabusSelectorCountrySelectorQuery {
    countries {
      id
      code
      name
    }
  }
`;

type Country =
  FreeTextbooksSyllabusSelectorCountrySelectorQuery['response']['countries'][number];

// We have locally extended the Country type to also have an 'international' code
type ExtendedCountry = Omit<Country, 'code'> & {
  code: CountryCodeEnum | 'international';
};

export function FreeTextbooksSyllabusSelectorCountrySelector() {
  return (
    <ErrorBoundaryWithRetry name="FreeTextbooksSyllabusSelectorCountrySelector">
      {({ fetchKey }) => (
        <Suspense fallback={<MinorSpinner />}>
          <FreeTextbooksSyllabusSelectorCountrySelectorInner
            fetchKey={fetchKey}
          />
        </Suspense>
      )}
    </ErrorBoundaryWithRetry>
  );
}

function FreeTextbooksSyllabusSelectorCountrySelectorInner({
  fetchKey,
}: {
  fetchKey: string;
}) {
  const data =
    useLazyLoadQuery<FreeTextbooksSyllabusSelectorCountrySelectorQuery>(
      query,
      {},
      {
        fetchKey,
        networkCacheConfig: {
          // @ts-expect-error TODO fix this once we upgrade relay and have sound types
          useCache: true,
          ttl: Infinity,
        },
      },
    );

  const { trackStructEvent } = useSnowplow();

  const countries: ExtendedCountry[] = [
    ...data.countries,
    {
      id: 'international',
      code: 'international',
      name: 'International',
    } as const,
  ];

  return (
    <Page>
      <PageDescription description={seo.INDEX.description} />
      <PageTitle subtitles={[seo.INDEX.title]} />
      <Title>Select a country</Title>
      <Separator size={4} />
      <Subtitle>Select a country to browse available curriculums</Subtitle>
      <Separator size={6} />
      <FlagsWrapper>
        {countries.map(c => (
          <ButtonFlagWrapper key={c.code}>
            <LinkButton
              styles={styles.stretch}
              color="cloudBurst"
              label={c.name}
              to={getCurriculumSelectorUrl({ countryCode: c.code })}
              onClick={() => {
                trackStructEvent({
                  category: 'free_textbook',
                  action: 'select_country',
                  property: 'countryId',
                  label: c.code,
                });
              }}
            >
              <FlagWrapper>
                <Flag alt={c.code} src={getFlagUrl(c.code)} />
                <FlagLabel>{c.name}</FlagLabel>
              </FlagWrapper>
            </LinkButton>
          </ButtonFlagWrapper>
        ))}
      </FlagsWrapper>
      <ContactUsWrapper>
        Learn more about Mathspace
        <BookDemoWrapper>
          <FreeTextbooksBookDemoButton
            buttonLabel="Chat to us"
            isRound
            type="secondary"
            snowplowEventLabel="country_selector"
            trackingId="FreeTextbook/CountrySelector"
          />
        </BookDemoWrapper>
      </ContactUsWrapper>
    </Page>
  );
}

function getFlagUrl(countryCode: ExtendedCountry['code']) {
  switch (countryCode) {
    case 'AU':
    case 'CA':
    case 'GB':
    case 'HK':
    case 'IN':
    case 'international':
    case 'NZ':
    case 'SG':
    case 'US':
      return FLAGS[countryCode];
    default:
      return FLAGS.GenericFlag;
  }
}
